import { capabilities } from 'navigation-components/capabilities';
import { queryLocalStorage } from 'navigation-components/common/utils';
import { ACADEMY_EXERCISE_CONTEXT_KEY, ACADEMY_EXERCISE_INSTRUCTIONS_COMPONENT, ACADEMY_TOOLBAR_COMPONENT, academyExerciseInstructionsNavComponentDef, academyToolbarNavComponentDef } from 'navigation-components/config';
import { useCapability, useComponentProps, useToggleComponent } from 'navigation-components/platform/hooks';
import { useCallback, useEffect } from 'react';
import { useAppSelector } from '../../../hooks';
import useEffectOnce from '../../../hooks/useEffectOnce';
import { selectGates } from '../../../reducers/Portal';
import memoWithDisplayName from '../../../utils/memoWithDisplayName';
import { getPortalId } from '../../../utils/getPortalId';
import { jsx as _jsx } from "react/jsx-runtime";
export const AcademyNavigationComponents = memoWithDisplayName('AcademyNavigationComponents', () => {
  const {
    toggle: toggleToolbar,
    onToggle: onToggleToolbar
  } = useToggleComponent(ACADEMY_TOOLBAR_COMPONENT);
  const {
    toggle: toggleInstructions
  } = useToggleComponent(ACADEMY_EXERCISE_INSTRUCTIONS_COMPONENT);
  const updateToolbarProps = useComponentProps(academyToolbarNavComponentDef);
  const updateInstructionsProps = useComponentProps(academyExerciseInstructionsNavComponentDef);
  const {
    subscribe: subscribeShowCap
  } = useCapability(capabilities.SHOW_COMPONENT_CAP.getName());
  const {
    subscribe: subscribeHideCap
  } = useCapability(capabilities.HIDE_COMPONENT_CAP.getName());
  const handleShowInstructions = useCallback(options => {
    if ((options === null || options === void 0 ? void 0 : options.byName) === ACADEMY_EXERCISE_INSTRUCTIONS_COMPONENT) {
      toggleInstructions(true);
      updateToolbarProps(current => Object.assign({}, current, {
        isInstructionsPopoverOpen: true
      }));
    }
  }, [toggleInstructions, updateToolbarProps]);
  const handleHideInstructions = useCallback(options => {
    if ((options === null || options === void 0 ? void 0 : options.byName) === ACADEMY_EXERCISE_INSTRUCTIONS_COMPONENT) {
      toggleInstructions(false);
      updateToolbarProps(current => Object.assign({}, current, {
        isInstructionsPopoverOpen: false
      }));
    }
  }, [toggleInstructions, updateToolbarProps]);
  useEffect(() => {
    const unsubscribeShowInstructions = subscribeShowCap(handleShowInstructions);
    const unsubscribeHideInstructions = subscribeHideCap(handleHideInstructions);
    const unsubscribeToggleToolbar = onToggleToolbar(isOpenToolbar => {
      if (!isOpenToolbar) {
        toggleInstructions(false);
      }
    });
    return () => {
      unsubscribeShowInstructions();
      unsubscribeHideInstructions();
      unsubscribeToggleToolbar();
    };
  }, [handleHideInstructions, handleShowInstructions, onToggleToolbar, subscribeHideCap, subscribeShowCap, toggleInstructions]);
  useEffectOnce(() => {
    const practicalExerciseContext = queryLocalStorage(ACADEMY_EXERCISE_CONTEXT_KEY);
    if (practicalExerciseContext && practicalExerciseContext.portalId === getPortalId()) {
      toggleToolbar(true);
      updateToolbarProps({
        name: practicalExerciseContext.name,
        id: practicalExerciseContext.id,
        lessonId: practicalExerciseContext.lessonId,
        trackId: practicalExerciseContext.trackId,
        portalId: practicalExerciseContext.portalId,
        sourcePortalId: practicalExerciseContext.sourcePortalId,
        isManuallyGraded: practicalExerciseContext.isManuallyGraded || false,
        isSimulation: practicalExerciseContext.isSimulation || false
      });
      updateInstructionsProps({
        description: practicalExerciseContext.description,
        exerciseLocation: practicalExerciseContext.exerciseLocation,
        isGraded: practicalExerciseContext.isGraded || false,
        isSimulation: practicalExerciseContext.isSimulation || false,
        isManuallyGraded: practicalExerciseContext.isManuallyGraded || false
      });
    }
  });
  return null;
});
export const MaybeLoadAcademyNavigationComponents = memoWithDisplayName('MaybeLoadAcademyNavigationComponents', () => {
  const gates = useAppSelector(selectGates);
  if (!gates.includes('Academy:NavComponents:Toolbar')) {
    return null;
  }
  return /*#__PURE__*/_jsx(AcademyNavigationComponents, {});
});